import { useCallback, useMemo, useRef, useState } from 'react';
import { onValidateField } from './_validators';

const onFieldCandidates = (field) => {
    const value = String(field || '').trim();
    if (!value) return [];
    const root = value.split('.')[0];
    return root === value ? [value] : [value, root];
};

const onFindFieldNode = (field) => {
    for (const candidate of onFieldCandidates(field)) {
        const safe = candidate.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        const byData = document.querySelector(`[data-field="${safe}"]`);
        if (byData) return byData;
        const byId = document.getElementById(candidate);
        if (byId) return byId;
    }
    return null;
};

export const SCROLL_TO_FORM_FIELD = (field, { behavior = 'smooth' } = {}) => {
    if (typeof document === 'undefined' || !field) return false;

    const NODE = onFindFieldNode(field);
    if (!NODE) return false;

    const MODAL_BODY = NODE.closest?.('.LStudio__app_modal__body');

    if (MODAL_BODY) {
        const nodeRect = NODE.getBoundingClientRect();
        const bodyRect = MODAL_BODY.getBoundingClientRect();
        const offset = Math.max(24, Math.min(120, MODAL_BODY.clientHeight * 0.18));
        const nextTop = MODAL_BODY.scrollTop + (nodeRect.top - bodyRect.top) - offset;
        MODAL_BODY.scrollTo({ top: Math.max(0, nextTop), behavior });
    } else {
        NODE.scrollIntoView?.({ behavior, block: 'center', inline: 'nearest' });
    }

    // El scroll suave necesita un instante antes del foco. preventScroll evita
    // que focus vuelva a mover el contenedor y esconda el mensaje de error.
    window.setTimeout(() => {
        try { NODE.focus?.({ preventScroll: true }); } catch { NODE.focus?.(); }
    }, behavior === 'smooth' ? 180 : 0);

    return true;
};

export const useForm = ({
    initialValues = {},
    schema = {},
    onSubmit = null,
    validateOnBlur = true,
    validateOnChange = true,
} = {}) => {
    const [useValues, setValues] = useState(initialValues);
    const [useErrors, setErrors] = useState({});
    const [useTouched, setTouched] = useState({});
    const [useIsSubmitting, setIsSubmitting] = useState(false);
    const [useSubmitCount, setSubmitCount] = useState(0);
    const [useServerError, setServerError] = useState(null);
    const [useRequestId, setRequestId] = useState(null);

    const REF__useRefInitial = useRef(initialValues);

    const CB__onValidateOne = useCallback((name, value, allValues) => (
        onValidateField(value, schema[name] ?? {}, allValues)
    ), [schema]);

    const CB__onValidateAll = useCallback((allValues) => {
        const NEXT = {};
        Object.keys(schema).forEach((name) => {
            const ERROR = CB__onValidateOne(name, allValues[name], allValues);
            if (ERROR) NEXT[name] = ERROR;
        });
        return NEXT;
    }, [schema, CB__onValidateOne]);

    const CB__onSetValue = useCallback((name, value) => {
        setServerError(null);
        setRequestId(null);

        setValues((prev) => {
            const NEXT = { ...prev, [name]: value };

            if (validateOnChange && useTouched[name]) {
                const ERROR = CB__onValidateOne(name, value, NEXT);
                setErrors((prevErrors) => ({ ...prevErrors, [name]: ERROR }));
            }

            return NEXT;
        });
    }, [useTouched, validateOnChange, CB__onValidateOne]);

    const CB__onChange = useCallback((event) => {
        const { name, type, value, checked } = event.target;
        CB__onSetValue(name, type === 'checkbox' ? checked : value);
    }, [CB__onSetValue]);

    const CB__onBlur = useCallback((event) => {
        const { name } = event.target;
        setTouched((prev) => ({ ...prev, [name]: true }));
        if (!validateOnBlur) return;

        setValues((current) => {
            const ERROR = CB__onValidateOne(name, current[name], current);
            setErrors((prev) => ({ ...prev, [name]: ERROR }));
            return current;
        });
    }, [validateOnBlur, CB__onValidateOne]);

    const CB__applyApiError = useCallback((apiError = {}, { focus = true } = {}) => {
        const FIELDS = apiError?.fields || apiError?.errors || apiError?.data?.errors || null;
        const MESSAGE = apiError?.message || apiError?.data?.message || null;
        const DETAIL = apiError?.detail || apiError?.data?.detail || null;
        const REQUEST_ID = apiError?.requestId || apiError?.data?.requestId || null;

        if (FIELDS && typeof FIELDS === 'object' && !Array.isArray(FIELDS)) {
            setErrors((prev) => ({ ...prev, ...FIELDS }));
            setTouched((prev) => {
                const NEXT = { ...prev };
                Object.keys(FIELDS).forEach((name) => { NEXT[name] = true; });
                return NEXT;
            });

            if (focus) {
                const NAMES = Object.keys(FIELDS);
                const FIRST_VISIBLE = NAMES.find((name) => !!onFindFieldNode(name));
                const FIRST = FIRST_VISIBLE || NAMES[0];
                if (FIRST) requestAnimationFrame(() => SCROLL_TO_FORM_FIELD(FIRST));
            }
        }

        const summary = [MESSAGE, DETAIL && DETAIL !== MESSAGE ? DETAIL : null].filter(Boolean).join(' ');
        setServerError(summary || null);
        setRequestId(REQUEST_ID || null);

        return { fields: FIELDS, message: summary, requestId: REQUEST_ID };
    }, []);

    const CB__onHandleSubmit = useCallback(async (event) => {
        event?.preventDefault?.();
        setSubmitCount((n) => n + 1);
        setServerError(null);
        setRequestId(null);

        const ERRORS = CB__onValidateAll(useValues);
        const ALL_TOUCHED = {};
        Object.keys(schema).forEach((name) => { ALL_TOUCHED[name] = true; });

        setTouched(ALL_TOUCHED);
        setErrors(ERRORS);

        if (Object.keys(ERRORS).length > 0) {
            const FIRST = Object.keys(ERRORS)[0];
            requestAnimationFrame(() => SCROLL_TO_FORM_FIELD(FIRST));
            return { isValid: false, errors: ERRORS };
        }

        if (!onSubmit) return { isValid: true, values: useValues };

        setIsSubmitting(true);
        try {
            const result = await onSubmit(useValues);
            return { isValid: true, values: useValues, result };
        } catch (err) {
            CB__applyApiError(err);
            return { isValid: false, error: err };
        } finally {
            setIsSubmitting(false);
        }
    }, [useValues, schema, CB__onValidateAll, CB__applyApiError, onSubmit]);

    const CB__onReset = useCallback((next = null) => {
        const VALUES = next ?? REF__useRefInitial.current;
        if (next) REF__useRefInitial.current = next;
        setValues(VALUES);
        setErrors({});
        setTouched({});
        setSubmitCount(0);
        setServerError(null);
        setRequestId(null);
    }, []);

    const CB__onSetError = useCallback((name, message, { focus = false } = {}) => {
        setErrors((prev) => ({ ...prev, [name]: message }));
        setTouched((prev) => ({ ...prev, [name]: true }));
        if (focus) requestAnimationFrame(() => SCROLL_TO_FORM_FIELD(name));
    }, []);

    const MEMO__IS_DIRTY = useMemo(() => (
        JSON.stringify(useValues) !== JSON.stringify(REF__useRefInitial.current)
    ), [useValues]);

    const MEMO__IS_VALID = useMemo(() => (
        Object.values(useErrors).every((e) => !e)
    ), [useErrors]);

    const CB__field = useCallback((name, options = {}) => ({
        id: name,
        name,
        value: useValues[name] ?? options.emptyValue ?? '',
        onChange: CB__onChange,
        onBlur: CB__onBlur,
        error: useTouched[name] ? (useErrors[name] ?? null) : null,
        isRequired: !!schema[name]?.required,
    }), [useValues, useErrors, useTouched, schema, CB__onChange, CB__onBlur]);

    const CB__fieldValue = useCallback((name, options = {}) => ({
        id: name,
        value: useValues[name] ?? options.emptyValue ?? null,
        onChange: (value) => CB__onSetValue(name, value),
        error: useTouched[name] ? (useErrors[name] ?? null) : null,
        isRequired: !!schema[name]?.required,
    }), [useValues, useErrors, useTouched, schema, CB__onSetValue]);

    return {
        values: useValues,
        errors: useErrors,
        touched: useTouched,
        isSubmitting: useIsSubmitting,
        isDirty: MEMO__IS_DIRTY,
        isValid: MEMO__IS_VALID,
        submitCount: useSubmitCount,
        serverError: useServerError,
        requestId: useRequestId,

        field: CB__field,
        fieldValue: CB__fieldValue,
        onChange: CB__onChange,
        onBlur: CB__onBlur,
        onSubmit: CB__onHandleSubmit,
        setValue: CB__onSetValue,
        setValues,
        setError: CB__onSetError,
        reset: CB__onReset,
        applyApiError: CB__applyApiError,
        clearApiError: () => { setServerError(null); setRequestId(null); },
        focusField: (name) => SCROLL_TO_FORM_FIELD(name),
        validate: () => CB__onValidateAll(useValues),
    };
};
